import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { RootState, store } from "../app/store";
import {
  currentIsSubstep,
  getLastNavigableSubStep as getLastSubStep,
  getNextNavigableStep as getNavigableStep,
  getNextNavigableSubStep as getNavigableSubStep,
  getPreviousNavigableSubStep as getPrevNavigableSubStep,
  isBackNavigationDisabled,
  isStepSkipped,
} from "../components/TemplateInterpreter/utils";
import { useNavigate } from "react-router-dom";
import {
  onUpdateCompletedForm as onUpdateCompletedFormOnboarding,
  onUpdateCompletedStep as onUpdateCompletedStepOnboarding,
  onUpdateDisabledSubStep,
  onUpdateFormData as onUpdateFormDataOnboarding,
  onUpdateLastCompletedSubStep as onUpdateLastCompletedSubStepOnboarding,
  onUpdateReachedStep as onUpdateReachedStepOnboarding,
  onUpdateStep as onUpdateStepOnboarding,
  onUpdateSubStep as onUpdateSubStepOnboarding,
} from "../reducers/onboardingDataReducer";
import {
  onSaveCurrentHolder,
  onUpdateCompletedForm as onUpdateCompletedFormSecurities,
  onUpdateCompletedStep as onUpdateCompletedStepSecurities,
  onUpdateFormData as onUpdateFormDataSecurities,
  onUpdateLastCompletedSubStep as onUpdateLastCompletedSubStepSecurities,
  onUpdateReachedStep as onUpdateReachedStepSecurities,
  onUpdateStep as onUpdateStepSecurities,
  onUpdateSubStep as onUpdateSubStepSecurities,
} from "../reducers/securitiesReducer";
import {
  utilsForLanguage,
  isBasicStep,
  isUnifiedOnboarding,
  isAllariaMasOnboarding,
} from "../utils";
import { onUpdateActiveTemplate } from "../reducers/activeTemplateReducer";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import { FormikProps, FormikValues } from "formik";
import { Step } from "../types";
import { onUpdateIsNavigating } from "../reducers/buttonsReducer";
import * as Sentry from "@sentry/react";
import {
  addStepBreadcrumb,
  addFormSubmitBreadcrumb,
  setSecuritiesContext,
  setSecuritiesTags,
} from "../sentry/utils";
import { fileToBase64, goToStep, showCustomToastifyError } from "../app/utils";
import { onUpdateIsFromNormalFlow } from "../reducers/preconfirmationReducer";
import { onUpdateInfoStep } from "../reducers/infoStepReducer";
import { isBlockedOnboardingError } from "../connectors/connectors";
import { isDraftBaseMismatchError } from "../utils/draftBase";

const useForm = (formik?: FormikProps<FormikValues>) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const state = useAppSelector((state) => state);

  const { values, setValues, setFieldValue } = formik || {};
  const {
    activeTemplate: template,
    onboardingData,
    securities,
    preconfirmation: { isEditingForm },
  } = state;

  const language = state?.securities?.language ?? "es";

  const {
    onUpdateCompletedForm,
    onUpdateCompletedStep,
    onUpdateFormData,
    onUpdateLastCompletedSubStep,
    onUpdateReachedStep,
    onUpdateStep,
    onUpdateSubStep,
  } = {
    onUpdateCompletedForm:
      template?.onboardingType === "SECURITIES"
        ? onUpdateCompletedFormSecurities
        : onUpdateCompletedFormOnboarding,
    onUpdateCompletedStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateCompletedStepSecurities
        : onUpdateCompletedStepOnboarding,
    onUpdateFormData:
      template?.onboardingType === "SECURITIES"
        ? onUpdateFormDataSecurities
        : onUpdateFormDataOnboarding,
    onUpdateLastCompletedSubStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateLastCompletedSubStepSecurities
        : onUpdateLastCompletedSubStepOnboarding,
    onUpdateReachedStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateReachedStepSecurities
        : onUpdateReachedStepOnboarding,
    onUpdateStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateStepSecurities
        : onUpdateStepOnboarding,
    onUpdateSubStep:
      template?.onboardingType === "SECURITIES"
        ? onUpdateSubStepSecurities
        : onUpdateSubStepOnboarding,
  };

  const {
    stepData: { step, subStep, completedSteps },
  } = template.onboardingType === "SECURITIES" ? securities : onboardingData;

  // Get current step based on main step
  const currentStep = useMemo(
    () => template?.steps?.find((s) => s.step === step),
    [step, template?.steps],
  );

  // Check if current step has substeps
  const currentIsSubStep = currentIsSubstep(currentStep);

  // Get numeric template steps
  const numericTemplateSteps = template?.steps?.filter(
    ({ step: templateStep }) => typeof templateStep === "number",
  );

  const lastTemplateStep = numericTemplateSteps?.length ?? 0;

  const getStepData = (target: number) =>
    template?.steps?.find((s) => s.step === target);

  const getNextNavigableStep = (
    candidate: number,
    currentState: RootState = state,
  ): number =>
    getNavigableStep(
      template?.steps,
      candidate,
      values,
      currentState,
      lastTemplateStep,
    );

  const getNextNavigableSubStep = (
    stepData: Step | undefined,
    candidate: number,
    currentState: RootState = state,
  ): number | null =>
    getNavigableSubStep(stepData, candidate, values, currentState);

  const getPreviousNavigableSubStep = (
    stepData: Step | undefined,
    candidate: number,
    currentState: RootState = state,
  ): number | null =>
    getPrevNavigableSubStep(stepData, candidate, values, currentState);

  const getLastNavigableSubStep = (
    stepData: Step | undefined,
    currentState: RootState = state,
  ): number | null => getLastSubStep(stepData, values, currentState);

  const nextNavigableSubStep = getNextNavigableSubStep(
    currentStep,
    Number(subStep) + 1,
  );

  // Current substep is the last one to show: every substep after it is skipped
  const isLastSubstep = currentIsSubStep && nextNavigableSubStep === null;

  // Check if current step is the last step
  const isLastStep = useMemo(() => {
    return (
      step ===
      template?.steps?.filter(({ step }) => typeof step === "number").length
    );
  }, [template?.steps, step]);

  // Check if current step is the last step and substep
  const isFinalStep = isLastStep && (!currentIsSubStep || isLastSubstep);

  // Unify steps and substeps
  const currentStepData = useMemo(() => {
    if (!isBasicStep(currentStep)) return currentStep;

    if ("subSteps" in currentStep) {
      return currentStep?.subSteps?.find((s) => s.step === (subStep || 1));
    } else {
      return currentStep;
    }
  }, [currentStep, subStep]);

  // Find previous step based on current step
  const findPreviousStep = () => {
    if (!numericTemplateSteps || !currentStep?.step) return null;
    const position = Number(currentStep.step) - 1;

    // Iterate backwards from the current step
    for (let index = position; index > 0; index--) {
      const element = numericTemplateSteps.find((s) => s.step === index);
      if (!element) continue;
      if (isStepSkipped(element, values, state)) continue;

      if (element.step === 1)
        return true; //This mean that the current step is TOKEN and we can go back

      const backNavigationDisabled = isBackNavigationDisabled(
        element.disableBackNavigation,
        values,
        completedSteps?.includes(index + 1) ? "completed" : "disabled",
      );

      if (!backNavigationDisabled) return element.step;
    }

    return null;
  };

  // Handle Back Button
  const handleBack = async () => {
    if (!currentStepData || !values || !setValues) return;

    // Crypto opens on the identity step, whose substeps go back among
    // themselves: leaving to "/" would drop /digital and its params.
    if (currentStep?.step === 1 && template.onboardingType !== "DIGITAL")
      navigate("/");

    if ("onBack" in currentStepData) {
      const latestState = store.getState();
      const res = await currentStepData.onBack?.(values, dispatch, latestState);

      if (res?.backStep) {
        dispatch(onUpdateStep(res.backStep));
      }

      const backSubStep =
        res?.backSubStep ??
        (res?.backStep
          ? getLastNavigableSubStep(
              getStepData(Number(res.backStep)),
              latestState,
            )
          : null);

      if (backSubStep) {
        dispatch(onUpdateSubStep(backSubStep));
      }
      return;
    }

    if (currentIsSubStep) {
      const previousSubStep = getPreviousNavigableSubStep(
        currentStep,
        Number(subStep) - 1,
      );

      if (previousSubStep) {
        dispatch(onUpdateSubStep(previousSubStep));
        return;
      }
    }

    const previousStep = findPreviousStep();
    if (!previousStep) return;

    dispatch(onUpdateStep(Number(previousStep)));

    const previousSubStep = getLastNavigableSubStep(
      getStepData(Number(previousStep)),
    );
    if (previousSubStep) dispatch(onUpdateSubStep(previousSubStep));
  };

  const getStepPosition = () => {
    const { stepData } =
      template.onboardingType === "SECURITIES"
        ? store.getState().securities
        : store.getState().onboardingData;

    return `${stepData.step}-${stepData.subStep}`;
  };

  const continueFlow = async () => {
    if (!currentStepData || !values || !setValues) return;

    const latestState = store.getState();

    // Add breadcrumb for form submission attempt (securities only)
    if (template?.onboardingType === "SECURITIES" && step !== null) {
      addStepBreadcrumb(step, "enter", { subStep, isLastStep });
      setSecuritiesContext(state);
      setSecuritiesTags(step, state.securities.currentHolder);
    }

    // Process form values to convert File objects to base64 strings
    const processFormValues = async (obj: unknown): Promise<unknown> => {
      if (obj === null || obj === undefined) {
        return obj;
      }

      if (obj instanceof File) {
        //If the file size is empty, means it was failed to upload, so we throw an error
        if (obj.size === 0) {
          throw new Error(`FILE_EMPTY::${obj.name}`);
        }

        let base64String = "";
        try {
          base64String = await fileToBase64(obj);
        } catch {
          throw new Error(`FILE_READ_ERROR::${obj.name}`);
        }

        return {
          base64String,
          name: obj.name,
          type: obj.type,
        };
      }

      if (Array.isArray(obj)) {
        return Promise.all(obj.map((item) => processFormValues(item)));
      }

      if (typeof obj === "object" && !(obj instanceof Date)) {
        const result: Record<string, unknown> = {};

        for (const key in obj as Record<string, unknown>) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = await processFormValues(
              (obj as Record<string, unknown>)[key],
            );
          }
        }
        return result;
      }

      return obj;
    };

    let processedValues: unknown;
    try {
      processedValues = await processFormValues(values);
    } catch (error) {
      Sentry.captureException(error);
      const errorMessage = error instanceof Error ? error.message : "";
      const toast = utilsForLanguage(language);

      if (errorMessage.startsWith("FILE_READ_ERROR::")) {
        const fileName = errorMessage.replace("FILE_READ_ERROR::", "");
        showToastMessage(toast.fileReadError(fileName), {
          containerId: "formAlert",
          type: "ERROR",
        });
      } else if (errorMessage.startsWith("FILE_EMPTY::")) {
        const fileName = errorMessage.replace("FILE_EMPTY::", "");
        showToastMessage(toast.fileEmptyError(fileName), {
          containerId: "formAlert",
          type: "ERROR",
        });
      } else {
        showToastMessage(toast.genericToastifyError, {
          containerId: "formAlert",
          type: "ERROR",
        });
      }
      return;
    }

    dispatch(onUpdateFormData(processedValues as FormikValues));

    const continueToPreconfirmation = () => {
      dispatch(
        onUpdateActiveTemplate({
          onboardingType: template?.onboardingType,
          preconfirmation: template?.preconfirmation,
        }),
      );
      dispatch(onUpdateCompletedForm(true));

      //If is unified onboarding, keep parameters in the URL
      if (isUnifiedOnboarding() && isAllariaMasOnboarding()) {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set("template", template?.onboardingType || "");
        dispatch(onUpdateIsFromNormalFlow(true));
        navigate(`/preconfirmacion?${currentParams.toString()}`);
      } else {
        dispatch(onUpdateIsFromNormalFlow(true));
        navigate(`/preconfirmacion?template=${template?.onboardingType}`);
      }

      if (template.onboardingType === "SECURITIES") {
        dispatch(onSaveCurrentHolder());
      }
      return;
    };

    const goToNextStep = () => {
      const freshState = store.getState();
      const nextStep = getNextNavigableStep(Number(step) + 1, freshState);

      if (nextStep > lastTemplateStep) return continueToPreconfirmation();

      const nextSubStep =
        getNextNavigableSubStep(getStepData(nextStep), 1, freshState) ?? 1;

      goToStep({ dispatch, step: nextStep, substep: nextSubStep });
    };

    if ("onContinue" in currentStepData) {
      try {
        if (!currentStepData?.onContinue) return;

        const res = await currentStepData?.onContinue(
          values,
          dispatch,
          latestState,
          setFieldValue,
        );

        if (!currentIsSubStep || isLastSubstep) {
          dispatch(onUpdateCompletedStep(Number(step)));
        }

        if (res?.newValuesToForm) {
          setValues((prevValues) => ({
            ...prevValues,
            ...res.newValuesToForm,
          }));
        }

        if (res?.nextStep || res?.nextSubStep) {
          if (res?.nextStep) {
            dispatch(onUpdateReachedStep(Number(res.nextStep)));
            dispatch(onUpdateStep(res.nextStep));
          }
          if (res?.nextSubStep) {
            dispatch(onUpdateSubStep(res.nextSubStep));
          }
          return;
        }

        if (res?.continueToPreconfirmation) {
          return continueToPreconfirmation();
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.log(error);

        // Add breadcrumb for failed submission
        if (template?.onboardingType === "SECURITIES" && step !== null) {
          addFormSubmitBreadcrumb(
            step,
            false,
            error instanceof Error ? error.message : "Unknown error",
          );
        }

        if (isBlockedOnboardingError(error)) return;

        if (latestState?.onboardingData?.draftUUID)
          Sentry.setTag("uuid", latestState.onboardingData?.draftUUID);
        if (error && typeof error === "object" && "response" in error) {
          Sentry.captureException(error.response.data);
          if (
            ["CXU_NOT_OWNER", "CXU_NOT_FOUND", "CUIT_NOT_CONFIRMED"].includes(
              error.response.data?.code,
            )
          )
            showCustomToastifyError(error.response.data.code);
          else if (isDraftBaseMismatchError(error.response.data))
            showCustomToastifyError("DRAFT_BASE_MISMATCH");
          else if (error.response.data?.code === "DRAFT_NOT_FOUND") {
            dispatch(onUpdateInfoStep("DRAFT_NOT_FOUND"));
            dispatch(onUpdateStep("information-step"));
          } else
            showToastMessage(utilsForLanguage(language).genericToastifyError, {
              containerId: "formAlert",
              type: "ERROR",
            });
        } else {
          // Securities errors are captured by captureSecuritiesError in each step
          if (!error.message.includes("SECURITIES_ERROR"))
            Sentry.captureException(error);
          if (["DNI_NO_VIGENTE", "INVALID_DATA_DNI"].includes(error.message))
            showCustomToastifyError(error.message);
          else
            showToastMessage(utilsForLanguage(language).genericToastifyError, {
              containerId: "formAlert",
              type: "ERROR",
            });
        }
        if (!currentIsSubStep || isLastSubstep) {
          dispatch(onUpdateCompletedStep(Number(step) - 1));
        }
        if (isLastSubstep) {
          dispatch(onUpdateLastCompletedSubStep(Number(subStep) - 1));
        }
        return;
      }
    }

    if (currentIsSubStep) {
      const nextSubstep = getNextNavigableSubStep(
        currentStep,
        Number(subStep) + 1,
        store.getState(),
      );

      if (nextSubstep) {
        dispatch(onUpdateLastCompletedSubStep(nextSubstep - 1));
        dispatch(onUpdateSubStep(nextSubstep));
      } else {
        // Juridical onboarding can't skip to preconfirmation because if corporateType changes, it affects following steps
        if (isEditingForm && template?.onboardingType !== "JURIDICAL") {
          continueToPreconfirmation();
        } else {
          dispatch(onUpdateCompletedStep(Number(step)));
          dispatch(onUpdateDisabledSubStep(null));

          if (!isLastStep) goToNextStep();
        }
      }
    } else {
      if (isEditingForm && template?.onboardingType !== "JURIDICAL") {
        continueToPreconfirmation();
      } else {
        if (!isLastStep) goToNextStep();
        dispatch(onUpdateDisabledSubStep(null));
      }
    }

    if (isFinalStep && template?.preconfirmation) {
      continueToPreconfirmation();
    }
  };

  const handleContinue = async () => {
    const positionBeforeContinue = getStepPosition();

    dispatch(onUpdateIsNavigating(true));

    try {
      await continueFlow();
    } finally {
      if (getStepPosition() === positionBeforeContinue) {
        dispatch(onUpdateIsNavigating(false));
      }
    }
  };

  return {
    currentStep,
    currentIsSubStep,
    numericTemplateSteps,
    isLastStep,
    currentStepData,
    findPreviousStep,
    handleBack,
    handleContinue,
  };
};

export default useForm;
